import React from 'react';
import { Text } from '@cotera/client/app/components/ui';
import { ToolOutputViewer } from '@cotera/client/v0/chat/tool-output-viewer';
import { WorkflowRunStatusBadge } from '../workflow-run-status';

/**
 * A step view's title row: the step's name and, when known, its status.
 *
 * Shared because the default, filter and agent views each carried an identical
 * copy of this markup down to the `!important` type overrides — so any change had
 * to be made three times, and a fourth step view would start by copying one.
 */
export const StepHeader: React.FC<{
  name: string;
  status: string | null;
}> = ({ name, status }) => (
  <div className="flex flex-wrap items-center gap-3">
    {/* Token colours, not `slate`: v1's neutrals are warm-biased (hue ~91) while
        Tailwind's slate is cool, so hardcoding it left step titles a visibly
        different grey from the rest of the app. */}
    <Text.Title type="title" className="text-foreground !text-2xl !font-bold">
      {name}
    </Text.Title>
    {status !== null ? (
      <WorkflowRunStatusBadge status={status} size="default" />
    ) : null}
  </div>
);

/** The error a step failed with, or nothing when it didn't. */
export const StepError: React.FC<{ error: string | null }> = ({ error }) =>
  error === null || error === '' ? null : (
    <pre className="overflow-auto whitespace-pre-wrap rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-xs text-destructive/90">
      {error}
    </pre>
  );

/** Titled section wrapper shared across run-step views. */
export const Section: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="space-y-3">
    <Text.P className="text-foreground font-semibold">{title}</Text.P>
    {children}
  </div>
);

/** Renders a hydrated payload, or a muted placeholder when absent. */
export const PayloadBlock: React.FC<{ value: unknown; emptyText: string }> = ({
  value,
  emptyText,
}) =>
  value === null || value === undefined ? (
    <p className="text-sm text-muted-foreground">{emptyText}</p>
  ) : (
    <ToolOutputViewer value={value} />
  );
